import { FavoriteButton } from '@components/MiscComponents/FavoriteButton';
import { Listing } from '@engelvoelkersamericas/api';
import { useCurrency } from '@util/customHooks/useCurrency';
import { useMeasurement } from '@util/customHooks/useMeasurement';
import usePropertySaver from '@util/customHooks/usePropertySaver';
import { buildListUrl } from '@util/listings';
import { useTranslations } from 'next-intl';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { FC } from 'react';
import {
  EmptyImagePlaceholder,
  FooterInfo,
  InfoHeaderWrapper,
  InfoHeadline,
  InfoItems,
  StyledInfoBoxCol,
  StyledInfoBoxImage,
  StyledInfoBoxMain,
  StyledInfoBoxWrapper,
} from './styles';
import { tPropertyType } from 'i18n/translation-utils';

interface ElasticListingMarkerProps {
  listing: Listing;
}

const ElasticListingMarker: FC<ElasticListingMarkerProps> = ({ listing }) => {
  const t = useTranslations();
  const { getListingPrice } = useCurrency();
  const { convertArea } = useMeasurement();
  const { isFav, onSaveProperty } = usePropertySaver({
    listingId: listing.ListingId,
    listOfficeMlsId: listing.ListOfficeMlsId ?? '',
  });

  const listingUrl = buildListUrl({
    StreetNumber: listing.UnparsedAddress?.split(' ')[0] ?? '',
    StreetName: listing.UnparsedAddress?.split(' ').slice(1).join(' ') ?? '',
    City: listing.City,
    StateOrProvince: listing.StateOrProvince,
    ZipCode: listing.PostalCode,
    OriginatingSystemName: listing.OriginatingSystemName,
    ListingId: listing.ListingId,
  });

  const { formattedPrice } = getListingPrice({
    standardStatus: listing.StandardStatus,
    listPrice: listing.ListPrice!,
    closePrice: listing.ClosePrice,
    closePriceToDisplay: listing.ClosePriceToDisplay,
    originatingSystemName: listing.OriginatingSystemName,
    country: listing.Country,
    listingCurrency: listing.CurrencyCode,
  });

  return (
    <StyledInfoBoxWrapper>
      <StyledInfoBoxMain>
        <StyledInfoBoxImage>
          <Link href={listingUrl}>
            {listing.MainPhotoUrl ? (
              <Image
                alt="Google Maps active marker"
                src={listing.MainPhotoUrl}
                width={135}
                height={135}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <EmptyImagePlaceholder />
            )}
          </Link>
        </StyledInfoBoxImage>
        <StyledInfoBoxCol>
          <InfoHeaderWrapper>
            <InfoHeadline>
              <Link href={listingUrl}>{formattedPrice}</Link>
            </InfoHeadline>

            <FavoriteButton $isFavorite={isFav} onClick={() => onSaveProperty()} />
          </InfoHeaderWrapper>
          <InfoItems>
            <Link href={listingUrl}>{listing.UnparsedAddress}</Link>
          </InfoItems>
          <InfoItems>
            {listing.PropertyType && <span>{tPropertyType(listing.PropertyType)}</span>}
            {listing.BedroomsTotal && (
              <span>
                {listing.BedroomsTotal} {t('Beds')}
              </span>
            )}
            {listing.BathroomsTotal && (
              <span>
                {listing.BathroomsTotal} {t('Baths')}
              </span>
            )}
            {listing.PropertyType === 'Land' && listing.LotSizeAcres ? (
              <span>
                {listing.LotSizeAcres} {t('Acres')}
              </span>
            ) : (
              listing.LotSizeSquareFeet && (
                <span>{convertArea(listing.LotSizeSquareFeet, 'sq.ft').formattedArea}</span>
              )
            )}
          </InfoItems>
          <FooterInfo>
            {t('Listing by')} {listing.ListOfficeName}
          </FooterInfo>
        </StyledInfoBoxCol>
      </StyledInfoBoxMain>
    </StyledInfoBoxWrapper>
  );
};

export default ElasticListingMarker;
